'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import ActionPlanDisplay from './ActionPlanDisplay';
import LinkedInGenerator from './LinkedInGenerator';
import CoverLetterGenerator from './CoverLetterGenerator';
import CareerProgressionTimeline from './CareerProgressionTimeline';
import ATSKeywords from './ATSKeywords';
import InterviewPrep from './InterviewPrep';
import SalaryNegotiation from './SalaryNegotiation';
import NetworkingScripts from './NetworkingScripts';
import PremiumToolkit from './PremiumToolkit';
import { formatCareerTitle } from '@/lib/utils/titleFormatter';
import styles from './PremiumToolsNavigator.module.css';

interface PremiumToolsNavigatorProps {
  career: CareerPath;
  userSegment?: string | null;
  hasCompleteTier: boolean;
  matchReasons?: string[];
}

type ToolId = 'action-plan' | 'linkedin' | 'cover-letter' | 'progression' | 'ats-keywords' | 'interview-prep' | 'salary-negotiation' | 'networking' | 'toolkit';

interface Tool {
  id: ToolId;
  name: string;
  icon: string;
  description: string;
  tier: 'blueprint' | 'complete';
  color: string;
}

const TOOLS: Tool[] = [
  {
    id: 'action-plan',
    name: '90-Day Action Plan',
    icon: '📅',
    description: 'Week-by-week roadmap with milestones',
    tier: 'blueprint',
    color: 'green',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Optimizer',
    icon: '💼',
    description: 'Headlines, summaries, and keywords',
    tier: 'blueprint',
    color: 'blue',
  },
  {
    id: 'cover-letter',
    name: 'Cover Letter Builder',
    icon: '📄',
    description: 'Templates and writing guidance',
    tier: 'blueprint',
    color: 'pink',
  },
  {
    id: 'progression',
    name: 'Career Timeline',
    icon: '📈',
    description: 'Salary growth and career ladder',
    tier: 'blueprint',
    color: 'yellow',
  },
  {
    id: 'ats-keywords',
    name: 'ATS Keywords',
    icon: '🔍',
    description: 'Resume optimization for ATS',
    tier: 'blueprint',
    color: 'blue',
  },
  {
    id: 'interview-prep',
    name: 'Interview Prep',
    icon: '🎯',
    description: 'Questions, STAR examples, and tips',
    tier: 'blueprint',
    color: 'teal',
  },
  {
    id: 'salary-negotiation',
    name: 'Salary Negotiation',
    icon: '💰',
    description: 'Scripts, market rates, and strategies',
    tier: 'blueprint',
    color: 'green',
  },
  {
    id: 'networking',
    name: 'Networking Scripts',
    icon: '🔗',
    description: 'LinkedIn outreach templates',
    tier: 'blueprint',
    color: 'blue',
  },
  {
    id: 'toolkit',
    name: 'Advanced Tools',
    icon: '⭐',
    description: 'Top employers, resources & more',
    tier: 'complete',
    color: 'purple',
  },
];

export default function PremiumToolsNavigator({
  career,
  userSegment = null,
  hasCompleteTier,
  matchReasons = [],
}: PremiumToolsNavigatorProps) {
  const [activeTool, setActiveTool] = useState<ToolId>('action-plan');

  const availableTools = TOOLS.filter(tool =>
    tool.tier === 'blueprint' || (tool.tier === 'complete' && hasCompleteTier)
  );

  const currentTool = TOOLS.find(t => t.id === activeTool);

  const renderActiveTool = () => {
    switch (activeTool) {
      case 'action-plan':
        return (
          <ActionPlanDisplay
            career={career}
            userSegment={userSegment}
            userStrengths={matchReasons}
          />
        );
      case 'linkedin':
        return (
          <LinkedInGenerator
            career={career}
            userSegment={userSegment}
          />
        );
      case 'cover-letter':
        return (
          <CoverLetterGenerator
            career={career}
            userSegment={userSegment}
          />
        );
      case 'progression':
        return <CareerProgressionTimeline career={career} />;
      case 'ats-keywords':
        return <ATSKeywords career={career} />;
      case 'interview-prep':
        return <InterviewPrep career={career} />;
      case 'salary-negotiation':
        return <SalaryNegotiation career={career} />;
      case 'networking':
        return <NetworkingScripts career={career} />;
      case 'toolkit':
        return hasCompleteTier ? (
          <PremiumToolkit
            careerId={career.id}
            careerTitle={formatCareerTitle(career.title)}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2>Your Career Launch Toolkit</h2>
          <p>Everything you need to land your {career.title} role</p>
        </div>
        <div className={styles.toolCount}>
          <span className={styles.toolCountNumber}>{availableTools.length}</span>
          <span className={styles.toolCountLabel}>Premium Tools</span>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.navigation}>
        <div className={styles.navScroll}>
          {availableTools.map((tool) => (
            <button
              key={tool.id}
              className={`${styles.navItem} ${activeTool === tool.id ? styles.activeNavItem : ''} ${styles[`color${tool.color}`]}`}
              onClick={() => setActiveTool(tool.id)}
            >
              <span className={styles.navIcon}>{tool.icon}</span>
              <span className={styles.navName}>{tool.name}</span>
              {tool.tier === 'complete' && (
                <span className={styles.completeBadge}>Complete</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Current Tool Info Bar */}
      {currentTool && (
        <div className={`${styles.toolInfo} ${styles[`bg${currentTool.color}`]}`}>
          <span className={styles.toolInfoIcon}>{currentTool.icon}</span>
          <div className={styles.toolInfoContent}>
            <span className={styles.toolInfoName}>{currentTool.name}</span>
            <span className={styles.toolInfoDescription}>{currentTool.description}</span>
          </div>
          <div className={styles.toolInfoNav}>
            <button
              className={styles.navArrow}
              onClick={() => {
                const currentIdx = availableTools.findIndex(t => t.id === activeTool);
                const prevIdx = (currentIdx - 1 + availableTools.length) % availableTools.length;
                setActiveTool(availableTools[prevIdx].id);
              }}
            >
              ←
            </button>
            <span className={styles.toolPosition}>
              {availableTools.findIndex(t => t.id === activeTool) + 1} / {availableTools.length}
            </span>
            <button
              className={styles.navArrow}
              onClick={() => {
                const currentIdx = availableTools.findIndex(t => t.id === activeTool);
                const nextIdx = (currentIdx + 1) % availableTools.length;
                setActiveTool(availableTools[nextIdx].id);
              }}
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Tool Content */}
      <div className={styles.toolContent}>
        {renderActiveTool()}
      </div>

      {/* Quick Jump */}
      <div className={styles.quickJump}>
        <span className={styles.quickJumpLabel}>Quick jump:</span>
        <div className={styles.quickJumpButtons}>
          {availableTools.map((tool) => (
            <button
              key={tool.id}
              className={`${styles.quickJumpButton} ${activeTool === tool.id ? styles.activeQuickJump : ''}`}
              onClick={() => setActiveTool(tool.id)}
              title={tool.name}
            >
              {tool.icon}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
